import React, { useEffect, useMemo, useState } from "react";
import api from "../../../lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, CheckCircle2, AlertTriangle } from "lucide-react";

// Routing-authority correction (Aug 2026) — this panel used to offer a
// single catalog-wide "Fulfillment" choice (Uber Delivery / Own
// Delivery / Online Order — Customer Pickup / Not using catalog
// fulfillment) that could override whichever fulfillment method the
// CUSTOMER actually selected (Section 1, `OrderModesPanel`) — the
// reported production defect: a customer's own Local Delivery
// selection was overridden by this catalog-wide "Customer Pickup"
// setting. It is now split into two independent, method-scoped
// sections, each rendered ONLY when its own customer fulfillment
// method (`order_modes`) is enabled:
//
//   * Local Delivery Provider — ONLY when `order_modes.delivery.
//     enabled`. Picks how the customer's OWN Local Delivery selection
//     is executed (Uber Direct vs Own Delivery). Never applies to
//     Pickup, Dine-In, Takeout, or Shipping orders.
//   * Online Customer Pickup Settings — ONLY when `order_modes.pickup.
//     enabled`. Configures the store address/instructions and whether
//     an online Pickup order must pay by card. Fully independent of
//     Local Delivery's provider choice — a catalog may use Own
//     Delivery for Local Delivery AND ordinary cash-friendly kiosk
//     Pickup at the same time.
//
// Both remain gated on `card_capture_mode === "tenant_review"` — the
// backend's own interlock (`retail/catalog.py::update_catalog`)
// already rejects either being set otherwise; this panel mirrors that
// rule client-side so the tenant sees why the controls are disabled
// rather than a raw 400 after clicking Save.

const LOCAL_DELIVERY_PROVIDERS = [
  { v: "uber", label: "Uber Direct", desc: "Rudras-AI requests a live Uber Direct quote for the customer's address." },
  { v: "own", label: "Own Delivery", desc: "You fulfill delivery yourself, priced by your own order-value/distance rules." },
];

// Gate C5A-1 (Sep 2026) — this function is the only thing standing
// between a dropped field and a false "Saved". It already existed, and
// it still reported success for the staging save that silently lost the
// preparation time, because it verified the provider and the override
// flag but simply did not look at `uber_default_preparation_minutes`.
//
// A round-trip verifier that checks SOME of what it submitted is not a
// verifier; it is a narrower way to lie. Every field this panel sends on
// a given branch is now compared against what the server echoed back.
function persistedMatchesLocalDeliveryIntent(saved, intent) {
  if (!saved) return false;
  if ((saved.local_delivery_provider ?? null) !== (intent.provider ?? null)) return false;
  if (intent.provider === "uber") {
    if (!!saved.allow_review_time_own_delivery !== !!intent.allowOverride) return false;
    // Compared numerically because the submitted value is coerced with
    // `Number(...)` at save time while the server returns a JSON int —
    // and compared at all because not comparing it is the defect.
    return Number(saved.uber_default_preparation_minutes ?? 0) === Number(intent.prepMinutes);
  }
  if (intent.provider === "own") {
    return Array.isArray(saved.own_delivery_rules) && saved.own_delivery_rules.length === intent.rules.length;
  }
  return true;
}

function persistedMatchesPickupIntent(saved, intent) {
  if (!saved) return false;
  if (!!saved.pickup_requires_online_card !== !!intent.requiresOnlineCard) return false;
  const addr = saved.pickup_store_address || {};
  return addr.line1 === intent.pickupAddressLine1.trim()
    && (addr.city || "") === intent.pickupCity.trim()
    && (addr.state || "") === intent.pickupState.trim()
    && (addr.postal_code || "") === intent.pickupPostal.trim()
    && (saved.pickup_instructions || "") === intent.pickupInstructions.trim();
}

function emptyRule() {
  return { id: `new-${Math.random().toString(36).slice(2, 10)}`, min_order_value: "0", max_order_value: "", max_distance_miles: "", fee: "", active: true };
}

function toNumber(v) {
  if (v === "" || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : NaN;
}

// Client-side mirror of `retail/delivery/own_delivery_rules.py::
// validate_own_delivery_rules` — never authoritative (the server
// re-validates on save), but catches every rejection case before a
// round trip so the tenant sees the exact reason immediately.
function validateRules(rules) {
  const errors = {};
  const parsed = rules.map((r) => ({
    id: r.id,
    min: toNumber(r.min_order_value),
    max: toNumber(r.max_order_value),
    dist: toNumber(r.max_distance_miles),
    fee: toNumber(r.fee),
    active: r.active,
  }));
  for (const p of parsed) {
    const rowErrors = [];
    if (p.min == null || Number.isNaN(p.min) || p.min < 0) rowErrors.push("Order-value minimum must be a non-negative number.");
    if (p.max == null || Number.isNaN(p.max) || p.max < 0) rowErrors.push("Order-value maximum is required and must be a non-negative number.");
    if (p.min != null && p.max != null && !Number.isNaN(p.min) && !Number.isNaN(p.max) && p.max < p.min) {
      rowErrors.push("Order-value maximum must be at least the minimum.");
    }
    if (p.dist == null || Number.isNaN(p.dist) || p.dist <= 0) rowErrors.push("Maximum driving distance must be a positive number of miles.");
    if (p.fee == null || Number.isNaN(p.fee) || p.fee < 0) rowErrors.push("Delivery fee must be a non-negative number.");
    if (rowErrors.length) errors[p.id] = rowErrors;
  }
  // Deterministic boundary behavior: both bounds are INCLUSIVE (matches
  // the server), so two active rules overlap when one's min falls at
  // or before the other's max.
  const active = parsed.filter((p) => p.active && !errors[p.id]).sort((a, b) => a.min - b.min);
  for (let i = 0; i < active.length - 1; i++) {
    if (active[i + 1].min <= active[i].max) {
      const msg = "This rule's order-value range overlaps another active rule.";
      errors[active[i].id] = [...(errors[active[i].id] || []), msg];
      errors[active[i + 1].id] = [...(errors[active[i + 1].id] || []), msg];
    }
  }
  return errors;
}

function LocalDeliveryProviderSection({ catalog, captureReady, onChanged }) {
  const [provider, setProvider] = useState(catalog?.local_delivery_provider ?? null);
  const [allowOverride, setAllowOverride] = useState(!!catalog?.allow_review_time_own_delivery);
  // Gate C5A — Uber Direct preparation time, held as the raw string the
  // input produced so a half-typed "1" is not silently re-rendered as
  // something else while the tenant is still typing. It is coerced to an
  // integer exactly once, at save.
  const [prepMinutes, setPrepMinutes] = useState(
    String(catalog?.uber_default_preparation_minutes ?? 0),
  );
  const [rules, setRules] = useState(() => (catalog?.own_delivery_rules?.length ? catalog.own_delivery_rules : [emptyRule()]));
  const [saving, setSaving] = useState(false);
  const [uberStatus, setUberStatus] = useState(null);
  const [routesConfigured, setRoutesConfigured] = useState(null);

  useEffect(() => {
    setProvider(catalog?.local_delivery_provider ?? null);
    setAllowOverride(!!catalog?.allow_review_time_own_delivery);
    setPrepMinutes(String(catalog?.uber_default_preparation_minutes ?? 0));
    setRules(catalog?.own_delivery_rules?.length ? catalog.own_delivery_rules : [emptyRule()]);
  }, [catalog?.id, catalog?.local_delivery_provider, catalog?.allow_review_time_own_delivery,
      catalog?.own_delivery_rules, catalog?.uber_default_preparation_minutes]);

  useEffect(() => {
    let alive = true;
    api.get("/retail/local-delivery/provider-config/uber-direct").then((r) => {
      if (alive) setUberStatus(r?.data || null);
    }).catch(() => { if (alive) setUberStatus(null); });
    api.get("/retail/local-delivery/config").then((r) => {
      if (alive) setRoutesConfigured(!!r?.data?.google_routes_configured);
    }).catch(() => { if (alive) setRoutesConfigured(null); });
    return () => { alive = false; };
  }, []);

  const ruleErrors = useMemo(() => (provider === "own" ? validateRules(rules) : {}), [provider, rules]);
  const hasRuleErrors = Object.keys(ruleErrors).length > 0;

  const isUberReadyToQuote = !!(
    uberStatus?.local_delivery_enabled
    && uberStatus?.provider_configured
    && uberStatus?.quote_enabled
  );

  const dirty = useMemo(() => {
    if (provider !== (catalog?.local_delivery_provider ?? null)) return true;
    if (provider === "uber") {
      return allowOverride !== !!catalog?.allow_review_time_own_delivery
        || Number(prepMinutes) !== Number(catalog?.uber_default_preparation_minutes ?? 0);
    }
    if (provider === "own") return JSON.stringify(rules) !== JSON.stringify(catalog?.own_delivery_rules || []);
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, allowOverride, rules, prepMinutes, catalog]);

  const addRule = () => setRules((rs) => [...rs, emptyRule()]);
  const removeRule = (id) => setRules((rs) => rs.filter((r) => r.id !== id));
  const updateRule = (id, field, value) => setRules((rs) => rs.map((r) => (r.id === id ? { ...r, [field]: value } : r)));

  // Routing-authority correction (Aug 2026, PR #141 corrected again,
  // Product Owner override) — Local Delivery requires an EXPLICIT
  // provider on every catalog, regardless of capture mode. "Uber
  // Direct" is the only provider that has ever functioned outside
  // Tenant Review capture (it is the pre-existing, always-available
  // dispatch path), so it is always selectable here. "Own Delivery"
  // still requires Tenant Review — its activation path only exists in
  // the tenant-review accept/capture flow; selecting it elsewhere
  // would be accepted by the backend's write-time interlock never (it
  // rejects the write), so the radio itself stays disabled until
  // `captureReady`, with an explanatory note rather than a blocking
  // banner over the whole section.
  const ownDeliveryAvailable = captureReady;

  const save = async () => {
    if (saving || !dirty) return;
    if (provider === "own" && !ownDeliveryAvailable) {
      toast.error('Own Delivery requires "Card payment capture" above to be set to "Tenant review" first.');
      return;
    }
    if (provider === "own" && hasRuleErrors) {
      toast.error("Fix the pricing rule errors before saving.");
      return;
    }
    if (provider === "uber") {
      const minutes = Number(prepMinutes);
      const valid = Number.isInteger(minutes)
        && (minutes === 0 || (minutes >= 5 && minutes <= 120));
      if (!valid) {
        toast.error("Preparation time must be 0 (not set), or between 5 and 120 minutes.");
        return;
      }
    }
    setSaving(true);
    try {
      const patch = { local_delivery_provider: provider };
      if (provider === "uber") {
        patch.allow_review_time_own_delivery = allowOverride;
        // Client-side check only — the server validates the same bounds
        // and REJECTS an out-of-range value rather than clamping it, so
        // this exists to give the tenant a fast, specific message, never
        // to be the rule.
        patch.uber_default_preparation_minutes = Number(prepMinutes);
      } else if (provider === "own") {
        patch.own_delivery_rules = rules.map((r) => ({
          id: r.id.startsWith("new-") ? undefined : r.id,
          min_order_value: r.min_order_value, max_order_value: r.max_order_value,
          max_distance_miles: r.max_distance_miles, fee: r.fee, active: r.active,
        }));
      }
      const resp = await api.put(`/retail/catalogs/${catalog.id}`, patch);
      const saved = resp?.data?.catalog;
      if (!persistedMatchesLocalDeliveryIntent(saved, {
        provider, allowOverride, rules, prepMinutes: Number(prepMinutes),
      })) {
        throw Object.assign(new Error("persistence_mismatch"), { persistenceMismatch: true });
      }
      toast.success("Local Delivery provider saved");
      await onChanged();
    } catch (e) {
      if (e?.persistenceMismatch) {
        toast.error("Save did not persist — please try again.");
      } else {
        const detail = e?.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : (detail?.message || "Could not save Local Delivery provider"));
      }
    } finally {
      setSaving(false);
    }
  };

  const saveDisabled = !dirty || saving || (provider === "own" && (!ownDeliveryAvailable || hasRuleErrors));

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="retail-catalog-local-delivery-provider-panel">
      <header className="flex items-center justify-between gap-3">
        <div>
          <h3 className="font-medium text-sm">Local Delivery Provider</h3>
          <p className="text-xs text-slate-500">
            Choose how your customers' Local Delivery orders are fulfilled. Customers never choose between Uber and Own Delivery — this setting decides it.
          </p>
        </div>
        <button
          type="button"
          onClick={save}
          disabled={saveDisabled}
          data-testid="retail-catalog-local-delivery-provider-save"
          className={"text-xs px-3 py-1.5 rounded-md font-medium " + (saveDisabled ? "bg-slate-200 text-slate-500 cursor-not-allowed" : "bg-emerald-600 text-white hover:bg-emerald-700")}
        >
          {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
        </button>
      </header>

      {!provider && (
        <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2" data-testid="retail-catalog-local-delivery-provider-required">
          Local Delivery is enabled but no provider is configured yet. Choose Uber Direct or Own Delivery below — Local Delivery checkout is blocked until you do.
        </div>
      )}

      <div className="space-y-2" data-testid="retail-catalog-local-delivery-provider-selector">
        {LOCAL_DELIVERY_PROVIDERS.map((t) => {
          const disabled = t.v === "own" && !ownDeliveryAvailable;
          return (
            <label
              key={t.v}
              className={"flex items-start gap-2 rounded-lg border p-3 " + (disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer") + " " + (provider === t.v ? "border-emerald-300 bg-emerald-50/40" : "border-slate-200")}
            >
              <input
                type="radio"
                name="local_delivery_provider"
                checked={provider === t.v}
                onChange={() => {
                  setProvider(t.v);
                  if (t.v !== "uber") setAllowOverride(false);
                }}
                disabled={disabled}
                data-testid={`retail-catalog-local-delivery-provider-${t.v}`}
                className="mt-0.5"
              />
              <div>
                <div className="text-sm font-medium text-slate-900">{t.label}</div>
                <div className="text-xs text-slate-500">{t.desc}</div>
                {disabled && (
                  <div className="text-[11px] text-amber-700 mt-1" data-testid="retail-catalog-local-delivery-provider-own-capture-gate">
                    Requires "Card payment capture" above to be set to "Tenant review".
                  </div>
                )}
              </div>
            </label>
          );
        })}
      </div>

      {provider === "uber" && (
        <div className="rounded-lg border border-slate-200 p-3 space-y-3" data-testid="retail-catalog-fulfillment-uber-config">
          <div className="flex items-center gap-2 text-xs" data-testid="retail-catalog-fulfillment-uber-status">
            {isUberReadyToQuote ? (
              <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> Uber Direct is configured on your account.</span>
            ) : (
              <span className="inline-flex items-center gap-1 text-amber-700"><AlertTriangle className="w-3.5 h-3.5" /> Uber Direct is not yet configured — set it up on the Local Delivery page first.</span>
            )}
          </div>
          {typeof uberStatus?.max_delivery_fee === "number" && (
            <div className="text-xs text-slate-500" data-testid="retail-catalog-fulfillment-uber-max-fee">
              Maximum delivery fee cap: ${Number(uberStatus.max_delivery_fee).toFixed(2)} (configured on the Local Delivery page)
            </div>
          )}
          {/* Gate C5A §5.1 — preparation time.

              0 means "not set", and is the default for every catalog:
              with 0 the Uber request is exactly what it has always been,
              so this field is purely additive and a tenant who ignores
              it sees no change at all. Any other value must be 5–120,
              which the server enforces independently (it rejects, never
              clamps — a "saved" value that reads back different has
              already caused one production incident in this panel). */}
          <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
            <label
              className="text-sm font-medium text-slate-900"
              htmlFor="uber-default-preparation-minutes"
            >
              Preparation time (minutes)
            </label>
            <div className="mt-1 flex items-center gap-2">
              <input
                id="uber-default-preparation-minutes"
                type="number"
                min={0}
                max={120}
                step={1}
                value={prepMinutes}
                onChange={(e) => setPrepMinutes(e.target.value)}
                data-testid="retail-catalog-fulfillment-uber-prep-minutes"
                className="w-24 rounded-md border border-slate-300 px-2 py-1 text-sm"
              />
              <span className="text-xs text-slate-500">0 = not set</span>
            </div>
            <p className="mt-1 text-xs text-slate-500">
              How long your kitchen needs before this order is ready for pickup.
              Rudras-AI asks Uber for a courier to arrive around that time, so the
              food is not sitting out. Allowed: 0, or 5&ndash;120 minutes.
            </p>
          </div>
          <label className="flex items-center justify-between gap-3 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 cursor-pointer">
            <div>
              <div className="text-sm font-medium text-slate-900">Allow "Accept &amp; Use Own Delivery" during review</div>
              <div className="text-xs text-slate-500">
                When enabled, you may fulfill an Uber-quoted order yourself instead — the customer's authorized Uber delivery charge is kept exactly as charged; no Uber dispatch is created.
              </div>
            </div>
            <input
              type="checkbox"
              checked={allowOverride}
              onChange={(e) => setAllowOverride(e.target.checked)}
              data-testid="retail-catalog-fulfillment-uber-override-toggle"
              className="h-4 w-4 accent-emerald-600"
            />
          </label>
          <p className="text-[11px] text-slate-400">Customers never see a provider selector — they only see the resulting Delivery Charge.</p>
        </div>
      )}

      {provider === "own" && (
        <div className="rounded-lg border border-slate-200 p-3 space-y-3" data-testid="retail-catalog-fulfillment-own-config">
          <div className="text-xs" data-testid="retail-catalog-fulfillment-routes-status">
            {routesConfigured === null ? null : routesConfigured ? (
              <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> Google Routes distance pricing is available.</span>
            ) : (
              <span className="inline-flex items-center gap-1 text-amber-700"><AlertTriangle className="w-3.5 h-3.5" /> Google Routes is not configured on this platform — Own Delivery checkout will fail closed (no fee, no fallback) until it is.</span>
            )}
          </div>
          <div className="space-y-2" data-testid="retail-catalog-fulfillment-own-rules">
            {rules.map((r) => (
              <div key={r.id} className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-start bg-slate-50 border border-slate-200 rounded-lg p-2" data-testid={`retail-own-delivery-rule-${r.id}`}>
                <div>
                  <div className="text-[10px] text-slate-500">Order min ($)</div>
                  <input type="number" min="0" step="0.01" value={r.min_order_value} onChange={(e) => updateRule(r.id, "min_order_value", e.target.value)} data-testid={`retail-own-delivery-rule-${r.id}-min`} className="w-full text-xs border border-slate-300 rounded px-2 py-1" />
                </div>
                <div>
                  <div className="text-[10px] text-slate-500">Order max ($)</div>
                  <input type="number" min="0" step="0.01" value={r.max_order_value} onChange={(e) => updateRule(r.id, "max_order_value", e.target.value)} data-testid={`retail-own-delivery-rule-${r.id}-max`} className="w-full text-xs border border-slate-300 rounded px-2 py-1" />
                </div>
                <div>
                  <div className="text-[10px] text-slate-500">Max distance (mi)</div>
                  <input type="number" min="0" step="0.1" value={r.max_distance_miles} onChange={(e) => updateRule(r.id, "max_distance_miles", e.target.value)} data-testid={`retail-own-delivery-rule-${r.id}-distance`} className="w-full text-xs border border-slate-300 rounded px-2 py-1" />
                </div>
                <div>
                  <div className="text-[10px] text-slate-500">Fee ($)</div>
                  <input type="number" min="0" step="0.01" value={r.fee} onChange={(e) => updateRule(r.id, "fee", e.target.value)} data-testid={`retail-own-delivery-rule-${r.id}-fee`} className="w-full text-xs border border-slate-300 rounded px-2 py-1" />
                </div>
                <div className="flex items-center gap-2 pt-4">
                  <label className="flex items-center gap-1 text-[10px] text-slate-600">
                    <input type="checkbox" checked={r.active} onChange={(e) => updateRule(r.id, "active", e.target.checked)} data-testid={`retail-own-delivery-rule-${r.id}-active`} /> Active
                  </label>
                  <button type="button" onClick={() => removeRule(r.id)} data-testid={`retail-own-delivery-rule-${r.id}-remove`} className="text-red-500 hover:text-red-700">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                {ruleErrors[r.id] && (
                  <div className="col-span-2 sm:col-span-5 text-[11px] text-red-600" data-testid={`retail-own-delivery-rule-${r.id}-error`}>
                    {ruleErrors[r.id].join(" ")}
                  </div>
                )}
              </div>
            ))}
            <button type="button" onClick={addRule} data-testid="retail-own-delivery-rule-add" className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-slate-300 hover:bg-slate-50">
              <Plus className="w-3.5 h-3.5" /> Add rule
            </button>
          </div>
          <p className="text-[11px] text-slate-400">
            Distance is computed fresh from Google Routes for every quote — never cached, never estimated with a straight-line distance.
          </p>
        </div>
      )}
    </section>
  );
}

function OnlinePickupSettingsSection({ catalog, captureReady, onChanged }) {
  const [requiresOnlineCard, setRequiresOnlineCard] = useState(!!catalog?.pickup_requires_online_card);
  const [pickupAddressLine1, setPickupAddressLine1] = useState(catalog?.pickup_store_address?.line1 || "");
  const [pickupCity, setPickupCity] = useState(catalog?.pickup_store_address?.city || "");
  const [pickupState, setPickupState] = useState(catalog?.pickup_store_address?.state || "");
  const [pickupPostal, setPickupPostal] = useState(catalog?.pickup_store_address?.postal_code || "");
  const [pickupInstructions, setPickupInstructions] = useState(catalog?.pickup_instructions || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setRequiresOnlineCard(!!catalog?.pickup_requires_online_card);
    setPickupAddressLine1(catalog?.pickup_store_address?.line1 || "");
    setPickupCity(catalog?.pickup_store_address?.city || "");
    setPickupState(catalog?.pickup_store_address?.state || "");
    setPickupPostal(catalog?.pickup_store_address?.postal_code || "");
    setPickupInstructions(catalog?.pickup_instructions || "");
  }, [catalog?.id, catalog?.pickup_requires_online_card, catalog?.pickup_store_address, catalog?.pickup_instructions]);

  const dirty = useMemo(() => {
    if (requiresOnlineCard !== !!catalog?.pickup_requires_online_card) return true;
    const before = catalog?.pickup_store_address || {};
    return pickupAddressLine1 !== (before.line1 || "") || pickupCity !== (before.city || "")
      || pickupState !== (before.state || "") || pickupPostal !== (before.postal_code || "")
      || pickupInstructions !== (catalog?.pickup_instructions || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requiresOnlineCard, pickupAddressLine1, pickupCity, pickupState, pickupPostal, pickupInstructions, catalog]);

  const save = async () => {
    if (saving || !dirty) return;
    if (requiresOnlineCard && !pickupAddressLine1.trim()) {
      toast.error("A store pickup address is required when online card payment is required.");
      return;
    }
    setSaving(true);
    try {
      const patch = {
        pickup_requires_online_card: requiresOnlineCard,
        pickup_store_address: {
          line1: pickupAddressLine1.trim(), city: pickupCity.trim(),
          state: pickupState.trim(), postal_code: pickupPostal.trim(),
        },
        pickup_instructions: pickupInstructions.trim(),
      };
      const resp = await api.put(`/retail/catalogs/${catalog.id}`, patch);
      const saved = resp?.data?.catalog;
      if (!persistedMatchesPickupIntent(saved, {
        requiresOnlineCard, pickupAddressLine1, pickupCity, pickupState, pickupPostal, pickupInstructions,
      })) {
        throw Object.assign(new Error("persistence_mismatch"), { persistenceMismatch: true });
      }
      toast.success("Online Customer Pickup settings saved");
      await onChanged();
    } catch (e) {
      if (e?.persistenceMismatch) {
        toast.error("Save did not persist — please try again.");
      } else {
        const detail = e?.response?.data?.detail;
        toast.error(typeof detail === "string" ? detail : (detail?.message || "Could not save Pickup settings"));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="retail-catalog-pickup-settings-panel">
      <header className="flex items-center justify-between gap-3">
        <div>
          <h3 className="font-medium text-sm">Online Customer Pickup Settings</h3>
          <p className="text-xs text-slate-500">
            Store address, instructions, and whether an online Pickup order must pay by card. Independent of your Local Delivery provider.
          </p>
        </div>
        <button
          type="button"
          onClick={save}
          disabled={!dirty || saving || !captureReady}
          data-testid="retail-catalog-pickup-settings-save"
          className={"text-xs px-3 py-1.5 rounded-md font-medium " + ((!dirty || saving || !captureReady) ? "bg-slate-200 text-slate-500 cursor-not-allowed" : "bg-emerald-600 text-white hover:bg-emerald-700")}
        >
          {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
        </button>
      </header>

      {!captureReady && (
        <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2" data-testid="retail-catalog-pickup-settings-capture-gate">
          Requiring online card payment for Pickup requires "Card payment capture" above to be set to "Tenant review" first. The store address/instructions below may still be saved for ordinary kiosk Pickup.
        </div>
      )}

      <label className="flex items-center justify-between gap-3 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 cursor-pointer">
        <div>
          <div className="text-sm font-medium text-slate-900">Require online card payment for Pickup</div>
          <div className="text-xs text-slate-500">
            When enabled, an online Pickup order must pay by card (never cash) and goes through the same tenant-review adjust/capture workflow as Local Delivery. Ordinary kiosk/walk-in Pickup keeps its own cash/card choice unless this is on.
          </div>
        </div>
        <input
          type="checkbox"
          checked={requiresOnlineCard}
          onChange={(e) => setRequiresOnlineCard(e.target.checked)}
          disabled={!captureReady}
          data-testid="retail-pickup-requires-online-card-toggle"
          className="h-4 w-4 accent-emerald-600"
        />
      </label>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <input placeholder="Store address line" value={pickupAddressLine1} onChange={(e) => setPickupAddressLine1(e.target.value)} data-testid="retail-pickup-address-line1" className="text-xs border border-slate-300 rounded px-2 py-1.5" />
        <input placeholder="City" value={pickupCity} onChange={(e) => setPickupCity(e.target.value)} data-testid="retail-pickup-address-city" className="text-xs border border-slate-300 rounded px-2 py-1.5" />
        <input placeholder="State" value={pickupState} onChange={(e) => setPickupState(e.target.value)} data-testid="retail-pickup-address-state" className="text-xs border border-slate-300 rounded px-2 py-1.5" />
        <input placeholder="Postal code" value={pickupPostal} onChange={(e) => setPickupPostal(e.target.value)} data-testid="retail-pickup-address-postal" className="text-xs border border-slate-300 rounded px-2 py-1.5" />
      </div>
      <textarea
        placeholder="Pickup instructions for the customer (e.g. 'Use the side door, ask for online orders')"
        value={pickupInstructions}
        onChange={(e) => setPickupInstructions(e.target.value)}
        maxLength={2000}
        data-testid="retail-pickup-instructions"
        className="w-full text-xs border border-slate-300 rounded px-2 py-1.5 h-20"
      />
      {requiresOnlineCard && (
        <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-3 py-2" data-testid="retail-pickup-confirmation-notice">
          Delivery charge: <strong>$0.00</strong>. Online payment is still required — this is not the unpaid kiosk/walk-in Pickup workflow.
        </div>
      )}
    </section>
  );
}

export default function CatalogFulfillmentPanel({ catalog, onChanged }) {
  const captureReady = catalog?.card_capture_mode === "tenant_review";
  const deliveryEnabled = !!catalog?.order_modes?.delivery?.enabled;
  const pickupEnabled = !!catalog?.order_modes?.pickup?.enabled;

  if (!deliveryEnabled && !pickupEnabled) {
    return null;
  }

  return (
    <div className="space-y-3" data-testid="retail-catalog-fulfillment-panel">
      {deliveryEnabled && (
        <LocalDeliveryProviderSection catalog={catalog} captureReady={captureReady} onChanged={onChanged} />
      )}
      {pickupEnabled && (
        <OnlinePickupSettingsSection catalog={catalog} captureReady={captureReady} onChanged={onChanged} />
      )}
    </div>
  );
}
